import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Camera as CameraIcon, RotateCcw, Loader2 } from 'lucide-react';
import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';
import { generateCosmeticUploadUrls, uploadCosmeticFile, checkCosmeticUploadedFiles, resolveFileType } from './CosmeticSkinCareApi';
import { LoginContext } from '../../MainLayout/MainLayout';

import aiTipsIcon from '../../Assets/Images/rewamp/skin/capture/ai tips.svg';
import autoIcon from '../../Assets/Images/rewamp/skin/capture/auto button within image capture.svg';
import captureEmptyIcon from '../../Assets/Images/rewamp/skin/capture/capture image empty state.svg';
import captureSuccessIcon from '../../Assets/Images/rewamp/skin/capture/capture photo button.svg';
import stepEmptyIcon from '../../Assets/Images/rewamp/skin/capture/capture steps empty state.svg';
import completeAllInfoIcon from '../../Assets/Images/rewamp/skin/capture/complete all 3 angles info.svg';
import continueArrowIcon from '../../Assets/Images/rewamp/skin/capture/continue arrow.svg';
import flipIcon from '../../Assets/Images/rewamp/skin/capture/flip button within image capture.svg';
import forBestResultIcon from '../../Assets/Images/rewamp/skin/capture/for best result.svg';
import goodLightingChipIcon from '../../Assets/Images/rewamp/skin/capture/good lighting.svg';
import holdSteadyChipIcon from '../../Assets/Images/rewamp/skin/capture/hold steady.svg';
import capturedTickIcon from '../../Assets/Images/rewamp/skin/capture/image captured tick mark.svg';
import useNaturalLightIcon from '../../Assets/Images/rewamp/skin/capture/use natural light.svg';
import keepFaceCenteredIcon from '../../Assets/Images/rewamp/skin/capture/keep your face centered.svg';
import noHeavyMakeupIcon from '../../Assets/Images/rewamp/skin/capture/no heavy makeup.svg';
import maintainNeutralExprIcon from '../../Assets/Images/rewamp/skin/capture/maintain neutral expr.svg';
import preferUploadIcon from '../../Assets/Images/rewamp/skin/capture/prefer uploading image.svg';
import uploadButtonIcon from '../../Assets/Images/rewamp/skin/capture/upload image button.svg';
import securePrivateIcon from '../../Assets/Images/rewamp/skin/capture/your photo private and secure.svg';

const STEPPER = ['Face Scan', 'Details', 'Analysis', 'Results'];

const ANGLES = [
  { key: 'front', title: 'Front', capStep: 'Capture straight face' },
  { key: 'left', title: 'Left Profile', capStep: 'Turn face to left' },
  { key: 'right', title: 'Right Profile', capStep: 'Turn face to right' },
];

const BEST_RESULT_TIPS = [
  { icon: useNaturalLightIcon, title: 'Use natural light', desc: 'Avoid harsh shadows' },
  { icon: keepFaceCenteredIcon, title: 'Keep your face centered', desc: 'Fit your face in the frame' },
  { icon: noHeavyMakeupIcon, title: 'No heavy makeup', desc: 'For accurate skin analysis' },
  { icon: maintainNeutralExprIcon, title: 'Maintain neutral expression', desc: 'Look straight at the camera' },
];

const BOTTOM_CHIPS = [
  { icon: goodLightingChipIcon, label: 'Good lighting' },
  { icon: holdSteadyChipIcon, label: 'Hold steady' },
  { icon: goodLightingChipIcon, label: 'Face clearly visible' },
  { icon: holdSteadyChipIcon, label: 'Avoid shadows' },
];

const FACE_GUIDE_STYLES = {
  loading: { color: '#8B86A8', message: 'Loading face guide…' },
  'no-face': { color: '#D14343', message: 'No face detected — position your face in the frame' },
  'multiple-faces': { color: '#D14343', message: 'Multiple faces detected — make sure only you are in frame' },
  'too-far': { color: '#C1580E', message: 'Move closer to the camera' },
  'too-close': { color: '#C1580E', message: 'Move back a little' },
  'off-center': { color: '#C1580E', message: 'Center your face within the frame' },
  good: { color: '#14804A', message: 'Perfect! Hold steady and capture' },
};

const evaluateFaceGuide = (faces, videoWidth, videoHeight) => {
  if (!faces.length) return { status: 'no-face', ...FACE_GUIDE_STYLES['no-face'] };
  if (faces.length > 1) return { status: 'multiple-faces', ...FACE_GUIDE_STYLES['multiple-faces'] };

  const box = faces[0].boundingBox;
  if (!box || !videoWidth || !videoHeight) return { status: 'good', ...FACE_GUIDE_STYLES.good };

  const sizeRatio = box.width / videoWidth;
  const centerX = (box.originX + box.width / 2) / videoWidth;
  const centerY = (box.originY + box.height / 2) / videoHeight;
  const offsetX = Math.abs(centerX - 0.5);
  const offsetY = Math.abs(centerY - 0.5);

  if (sizeRatio < 0.15) return { status: 'too-far', ...FACE_GUIDE_STYLES['too-far'] };
  if (sizeRatio > 0.65) return { status: 'too-close', ...FACE_GUIDE_STYLES['too-close'] };
  if (offsetX > 0.18 || offsetY > 0.18) return { status: 'off-center', ...FACE_GUIDE_STYLES['off-center'] };
  return { status: 'good', ...FACE_GUIDE_STYLES.good };
};

const dataUrlToFile = (dataUrl, filename) => {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
};

// Gallery-picked photos come in at whatever resolution the phone's camera used
// (often 3-15MB) with no size cap, unlike the live capture path which is
// bounded by getUserMedia's ideal 1280x960 constraint. Re-encoding through a
// canvas here normalizes every source (including HEIC, which browsers that
// support it will decode into the <img> just fine) into a real, correctly
// labeled JPEG capped to a sane size — this is what keeps the 3-image set
// small enough to survive sessionStorage's ~5-10MB quota later.
const MAX_IMAGE_DIMENSION = 1600;
const IMAGE_QUALITY = 0.85;
const resizeImageFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read the selected file.'));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('Could not decode the selected image.'));
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        const scale = MAX_IMAGE_DIMENSION / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', IMAGE_QUALITY));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const getStatusMessage = (currentAngle, images, allDone) => {
  if (allDone) {
    return 'Images from all three angles completed successfully! We can continue for further steps.';
  }
  const remaining = ANGLES.filter((a) => a.key !== currentAngle.key && !images[a.key]).map((a) => a.title);
  if (remaining.length === 0) {
    return `${currentAngle.title} image successfully captured!`;
  }
  return `${currentAngle.title} image successfully captured! Now capture ${remaining.join(' and ')} to continue.`;
};

const CosmeticFaceCapture = () => {
  const navigate = useNavigate();
  const { openLogin } = useContext(LoginContext);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);
  const faceDetectorRef = useRef(null);
  const faceDetectionIntervalRef = useRef(null);
  const cameraGenerationRef = useRef(0);

  const loginObj = JSON.parse(localStorage.getItem('loginObj') || 'null');

  // Backstop for anyone who lands here directly (deep link, bookmark, shared
  // URL) without going through the Cosmetic landing page's own login gate —
  // this is what leaves patientId missing from the upload payload.
  useEffect(() => {
    if (!loginObj?.code && !loginObj?.patientId) {
      openLogin();
      navigate('/cosmetic-skincare-analysis');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore any images captured earlier in this session (e.g. the user went
  // "Previous" from the Details page back to Face Scan) instead of losing them.
  const [images, setImages] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('cosmeticFaceImages') || 'null');
      if (saved && typeof saved === 'object') {
        return { front: saved.front || null, left: saved.left || null, right: saved.right || null };
      }
    } catch {}
    return { front: null, left: null, right: null };
  });
  const [stepIndex, setStepIndex] = useState(() => {
    const firstMissing = ANGLES.findIndex((a) => !images[a.key]);
    return firstMissing === -1 ? ANGLES.length - 1 : firstMissing;
  });
  const [phase, setPhase] = useState(() => (images[ANGLES[stepIndex]?.key] ? 'captured' : 'live'));
  const [facingMode, setFacingMode] = useState('user');
  const [cameraError, setCameraError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [modelReady, setModelReady] = useState(false);
  const [faceGuide, setFaceGuide] = useState({ status: 'loading', ...FACE_GUIDE_STYLES.loading });
  const [captureNotReady, setCaptureNotReady] = useState(false);

  const capturedCount = Object.values(images).filter(Boolean).length;
  const allDone = capturedCount === ANGLES.length;
  const canContinue = allDone; // all 3 angles are required before continuing
  const currentAngle = ANGLES[stepIndex];
  const nextAngle = ANGLES.find((a) => a.key !== currentAngle.key && !images[a.key]) || null;

  const startCamera = useCallback(async (mode = facingMode) => {
    setCameraError('');
    const myGeneration = ++cameraGenerationRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      // If unmounted, or a newer startCamera call has since superseded this one
      // (e.g. rapid angle/flip changes), this stream is stale — release it
      // immediately instead of leaking an orphaned active camera track.
      if (!isMountedRef.current || cameraGenerationRef.current !== myGeneration) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      if (isMountedRef.current) {
        setCameraError('Camera access denied or not available. Please upload a photo instead.');
      }
    }
  }, [facingMode]);

  useEffect(() => {
    if (phase === 'live' && !allDone) {
      startCamera(facingMode);
    }
    return () => {
      if (allDone && streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, stepIndex]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Load the (free, ~200KB, client-side-only) face detection model once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm'
        );
        const baseOptions = {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite',
        };
        let detector;
        try {
          detector = await FaceDetector.createFromOptions(vision, {
            baseOptions: { ...baseOptions, delegate: 'GPU' },
            runningMode: 'VIDEO',
            minDetectionConfidence: 0.5,
            minSuppressionThreshold: 0.3,
          });
        } catch {
          // Some browsers/devices don't support the GPU delegate for WASM — fall back to CPU.
          detector = await FaceDetector.createFromOptions(vision, {
            baseOptions: { ...baseOptions, delegate: 'CPU' },
            runningMode: 'VIDEO',
            minDetectionConfidence: 0.5,
            minSuppressionThreshold: 0.3,
          });
        }
        if (cancelled) {
          detector.close();
          return;
        }
        faceDetectorRef.current = detector;
        setModelReady(true);
      } catch (err) {
        console.error('Failed to load face detector:', err);
      }
    })();
    return () => {
      cancelled = true;
      if (faceDetectorRef.current) {
        faceDetectorRef.current.close();
        faceDetectorRef.current = null;
      }
    };
  }, []);

  // Reset the guide message whenever we (re)enter live camera mode for a new angle.
  useEffect(() => {
    if (phase === 'live' && !allDone) {
      setFaceGuide({ status: 'loading', ...FACE_GUIDE_STYLES.loading });
    }
  }, [stepIndex, phase, allDone]);

  // Poll the video feed for a face while it's live, and turn that into
  // plain-language guidance (no face / too close / off-center / perfect).
  useEffect(() => {
    if (!modelReady || phase !== 'live' || allDone || cameraError) {
      return undefined;
    }
    faceDetectionIntervalRef.current = setInterval(() => {
      const detector = faceDetectorRef.current;
      const video = videoRef.current;
      if (!detector || !video || video.readyState < 2 || !video.videoWidth) return;
      let result;
      try {
        result = detector.detectForVideo(video, performance.now());
      } catch {
        return;
      }
      const faces = result?.detections || [];
      setFaceGuide(evaluateFaceGuide(faces, video.videoWidth, video.videoHeight));
    }, 350);
    return () => {
      if (faceDetectionIntervalRef.current) {
        clearInterval(faceDetectionIntervalRef.current);
        faceDetectionIntervalRef.current = null;
      }
    };
  }, [modelReady, phase, allDone, cameraError, stepIndex]);

  const handleFlip = () => {
    const nextMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(nextMode);
    startCamera(nextMode);
  };

  const applyCapturedImage = (dataUrl) => {
    setImages((prev) => ({ ...prev, [currentAngle.key]: dataUrl }));
    setPhase('captured');
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    // Guards against a blank/black capture when the button is tapped before
    // the camera stream actually has a decoded frame ready (readyState < 2
    // means no data, or videoWidth 0 means metadata hasn't loaded) — this is
    // what caused capture to silently "not work" on some phones/networks.
    // Uses its own state rather than cameraError, which unmounts the <video>.
    if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      setCaptureNotReady(true);
      setTimeout(() => setCaptureNotReady(false), 2000);
      return;
    }
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    // The front-camera preview is mirrored (CSS transform) so it feels like
    // looking in a mirror, but drawImage always pulls the raw, unmirrored
    // stream data — flip it here too so the saved photo matches what the
    // user actually saw on screen when they tapped capture.
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    applyCapturedImage(canvas.toDataURL('image/jpeg', 0.92));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const isImage = file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/heic' || file.type === 'image/heif' || /\.(jpe?g|png|heic|heif)$/i.test(file.name || '');
    if (!isImage) {
      setUploadError('Please choose a JPG, PNG, or HEIC photo.');
      return;
    }
    const isUnderLimit = file.size / 1024 / 1024 < 20;
    if (!isUnderLimit) {
      setUploadError('That photo is too large. Please choose one under 20MB.');
      return;
    }
    setUploadError('');
    try {
      const resized = await resizeImageFile(file);
      applyCapturedImage(resized);
    } catch {
      setUploadError('Could not process that photo. Please try a different one.');
    }
  };

  const handleJumpToAngle = (index) => {
    setStepIndex(index);
    setPhase('live');
  };

  const handleGoToNext = (angle) => {
    if (!angle) return;
    setStepIndex(ANGLES.findIndex((a) => a.key === angle.key));
    setPhase('live');
  };

  const handleRetake = () => {
    setImages((prev) => ({ ...prev, [currentAngle.key]: null }));
    setPhase('live');
  };

  const handleContinueToForm = async () => {
    if (!canContinue || isUploading) return;
    setUploadError('');
    setIsUploading(true);
    try {
      const capturedAngles = ANGLES.filter((a) => images[a.key]);
      const files = capturedAngles.map((a, i) => dataUrlToFile(images[a.key], `${a.key}_${i + 1}.jpg`));

      const { requestId, uploadUrls } = await generateCosmeticUploadUrls(loginObj?.code, files);
      if (!requestId) throw new Error('No requestId returned from upload URL request');

      for (let i = 0; i < files.length; i += 1) {
        const url = uploadUrls[i]?.url;
        if (!url) throw new Error(`Missing upload URL for file ${i + 1}`);
        await uploadCosmeticFile(url, files[i]);
      }

      await checkCosmeticUploadedFiles({
        requestId,
        sourceChannel: loginObj?.sourcechannel,
        createdBy: loginObj?.createdBy,
        signedUrlCount: files.length,
        fileType: resolveFileType(files[0]),
      });

      // requestId is required by every later step — save it on its own so a
      // quota failure on the (best-effort, preview-only) face images below can
      // never take it down too.
      try { sessionStorage.setItem('cosmeticRequestId', requestId); } catch {}
      try { sessionStorage.setItem('cosmeticFaceImages', JSON.stringify(images)); } catch {}

      navigate('/cosmetic-skincare-analysis/details');
    } catch (err) {
      setUploadError('Upload failed. Please check your connection and try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mx-auto w-full">
      <style>{`
        .fc-wrapper {
          background-color: #fff; background-image: var(--section-bg-gradient);
          padding: 24px max(40px, calc(50% - 680px)) 48px;
        }

        .fc-header {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          margin-bottom: 24px;
        }

        .fc-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 16px;
          color: #1A1A2E;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          justify-self: start;
        }

        .fc-stepper {
          display: flex;
          align-items: flex-start;
          gap: 18px;
        }

        .fc-stepper-caption {
          font-size: 14px;
          color: #676C80;
          margin-top: 5px;
          white-space: nowrap;
        }

        .fc-stepper-track {
          display: flex;
          align-items: center;
        }

        .fc-stepper-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          width: 72px;
        }

        .fc-stepper-circle {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          background: #E6E3F0;
          border: 1.5px solid #E6E3F0;
          color: #8B86A8;
        }

        .fc-stepper-item.active .fc-stepper-circle {
          background: var(--color-brand-green);
          border-color: var(--color-brand-green);
          color: #ffffff;
          box-shadow: 0 0 0 4px #00BD7F26;
        }

        .fc-stepper-label {
          font-size: 12px;
          font-weight: 500;
          color: #8B86A8;
        }

        .fc-stepper-item.active .fc-stepper-label {
          font-weight: 600;
          color: #1A1A2E;
        }

        .fc-stepper-line {
          width: 28px;
          height: 1.5px;
          background: #D9D5E6;
          margin-top: 14px;
          align-self: flex-start;
        }

        .fc-content {
          display: flex;
          gap: 24px;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        /* Shared card look — light blue border with a soft glow (per Figma) */
        .fc-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 20px;
          border: 1px solid #C9EEF8;
          box-shadow: 0px 4px 12px 0px #80DAF14D;
        }

        /* LEFT COLUMN */
        .fc-left {
          flex: 1 1 260px;
          max-width: 290px;
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .fc-left-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          font-size: 26px;
          line-height: 34px;
          color: var(--color-brand-primary);
          margin-bottom: 10px;
        }

        .fc-left-heading-accent {
          display: block;
          color: var(--color-brand-green);
        }

        .fc-left-desc {
          font-size: 14px;
          line-height: 1.55;
          color: var(--color-brand-primary);
        }

        .fc-tips-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          font-size: 14px;
          color: var(--color-brand-primary);
          margin-bottom: 12px;
        }

        .fc-tips-list {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .fc-tip-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .fc-tip-icon {
          width: 34px;
          height: 34px;
          flex-shrink: 0;
        }

        .fc-tip-title {
          font-weight: 600;
          font-size: 13px;
          color: var(--color-brand-primary);
        }

        .fc-tip-desc {
          font-size: 12px;
          color: #676C80;
        }

        .fc-upload-card {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .fc-upload-icon {
          width: 34px;
          height: 34px;
          flex-shrink: 0;
        }

        .fc-upload-title {
          font-weight: 600;
          font-size: 13px;
          color: var(--color-brand-primary);
        }

        .fc-upload-desc {
          font-size: 12px;
          color: #676C80;
        }

        /* CENTER COLUMN */
        .fc-center {
          flex: 2 1 420px;
          min-width: 320px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        /* Camera + its buttons live in one card; the frame has a capped height so the
           buttons, tips and Continue stay on screen instead of below the fold. */
        .fc-camera-card {
          background: #ffffff;
          border-radius: 20px;
          border: 1px solid #C9EEF8;
          box-shadow: 0px 4px 12px 0px #80DAF14D;
          overflow: hidden;
        }

        .fc-camera-frame {
          position: relative;
          width: 100%;
          height: clamp(300px, 48vh, 440px);
          background: #C9C4D9;
          overflow: hidden;
        }

        .fc-camera-bottom {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 18px 20px 16px 20px;
          background: linear-gradient(180deg, #F3F9FE 0%, #FFFFFF 100%);
        }

        .fc-camera-video, .fc-camera-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .fc-camera-video.mirrored {
          transform: scaleX(-1);
        }

        .fc-face-guide-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .fc-face-guide-svg ellipse {
          transition: stroke 0.3s ease, filter 0.3s ease;
        }


        .fc-camera-top-controls {
          position: absolute;
          top: 12px;
          left: 12px;
          right: 12px;
          display: flex;
          justify-content: space-between;
          z-index: 2;
        }

        .fc-pill-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255,255,255,0.9);
          border: none;
          border-radius: 999px;
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
          color: #1A1A2E;
          cursor: pointer;
        }

        .fc-status-banner {
          position: absolute;
          left: 50%;
          bottom: 16px;
          transform: translateX(-50%);
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(43, 38, 64, 0.85);
          color: #ffffff;
          font-size: 13px;
          font-weight: 500;
          padding: 10px 18px;
          border-radius: 999px;
          max-width: 90%;
          text-align: center;
          z-index: 2;
        }

        .fc-camera-error {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          text-align: center;
          color: var(--color-text-subheading);
          font-size: 13px;
        }

        .fc-camera-actions {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 16px;
          max-width: 460px;
          width: 100%;
          margin: 0 auto;
        }

        .fc-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          font-weight: 600;
          font-size: 14px;
          color: #ffffff;
          background: var(--color-brand-primary);
          border: none;
          border-radius: 999px;
          padding: 12px 24px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .fc-btn-primary:hover { background: var(--color-brand-primary-hover); }

        .fc-btn-outline-wrapper {
          display: block;
          border-radius: 999px;
          padding: 1.5px;
          background: var(--color-brand-primary);
        }

        .fc-btn-outline {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          font-weight: 600;
          font-size: 14px;
          background: #ffffff;
          border-radius: 999px;
          padding: 10.5px 22px;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
        }
        .fc-btn-outline:hover { background: #EAF3FA; }

        .fc-btn-outline-text {
          color: var(--color-brand-primary);
        }

        .fc-secure-note {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 12px;
          color: #676C80;
        }

        .fc-chip-row {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          background: #ffffff;
          border-radius: 16px;
          padding: 14px 16px;
          border: 1px solid #C9EEF8;
          box-shadow: 0px 4px 12px 0px #80DAF14D;
        }

        .fc-chip {
          flex: 1 1 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
        }

        .fc-chip-ai {
          background: #EEEAFD;
          color: #6D4AD8;
        }

        .fc-chip-good {
          background: #E4F9EC;
          color: #14804A;
        }

        .fc-chip-hold {
          background: #FFEEE0;
          color: #C1580E;
        }

        .fc-chip-icon {
          width: 16px;
          height: 16px;
        }

        /* RIGHT COLUMN */
        .fc-right {
          flex: 1 1 260px;
          max-width: 300px;
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .fc-card-title {
          font-weight: 700;
          font-size: 16px;
          color: var(--color-brand-primary);
          margin-bottom: 2px;
        }

        .fc-card-subtitle {
          font-size: 12px;
          color: #676C80;
          margin-bottom: 14px;
        }

        .fc-step-row {
          display: flex;
          align-items: center;
          gap: 10px;
          border: 1px solid #DFF1FB;
          border-radius: 12px;
          padding: 10px 12px;
          margin-bottom: 10px;
          width: 100%;
          text-align: left;
          background: #ffffff;
          font-family: inherit;
          cursor: pointer;
        }

        .fc-step-row:hover {
          border-color: #80DAF1;
        }

        .fc-step-row.current:not(.done) {
          border-color: #80DAF1;
          background: #F1F8FD;
        }

        .fc-step-row.done {
          border: 1.5px solid var(--color-brand-green);
          background: #F2FBF7;
        }

        .fc-step-icon {
          width: 40px;
          height: 40px;
          flex-shrink: 0;
        }

        .fc-step-title {
          font-weight: 600;
          font-size: 13px;
          color: #1A1A2E;
        }

        .fc-step-desc {
          font-size: 11px;
          color: #676C80;
        }

        .fc-step-check {
          width: 20px;
          height: 20px;
          margin-left: auto;
          flex-shrink: 0;
        }

        .fc-thumbs-caption {
          font-size: 12px;
          color: #676C80;
          margin-bottom: 12px;
        }

        .fc-thumbs-row {
          display: flex;
          gap: 10px;
          margin-bottom: 8px;
        }

        .fc-thumb {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          background: none;
          border: none;
          padding: 0;
          font-family: inherit;
          cursor: pointer;
        }

        .fc-thumb-box {
          position: relative;
          width: 100%;
          aspect-ratio: 4 / 5;
          border-radius: 10px;
          overflow: hidden;
          background: #EAF3FA;
        }

        .fc-thumb-box img.fc-thumb-empty {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .fc-thumb-box img.fc-thumb-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .fc-thumb-tick {
          position: absolute;
          top: 4px;
          right: 4px;
          width: 18px;
          height: 18px;
        }

        .fc-thumb-label {
          font-size: 12px;
          color: #676C80;
        }

        .fc-thumb-label.done {
          font-weight: 700;
          color: var(--color-brand-green);
        }

        .fc-continue-btn {
          width: 100%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-weight: 600;
          font-size: 14px;
          border-radius: 999px;
          padding: 12px;
          border: none;
          cursor: not-allowed;
          background: #EAF3FA;
          color: #9490A8;
        }

        .fc-continue-btn.enabled {
          cursor: pointer;
          background: var(--color-brand-primary);
          color: #ffffff;
          box-shadow: 0px 4px 12px 0px #00427633;
        }

        .fc-spin {
          animation: fc-spin 0.8s linear infinite;
        }

        @keyframes fc-spin {
          to { transform: rotate(360deg); }
        }

        .fc-continue-caption {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 11px;
          color: #9490A8;
          margin-top: 8px;
          text-align: center;
        }

        @media (max-width: 1180px) {
          .fc-left, .fc-right { max-width: 100%; flex-basis: 100%; }
        }

        @media (max-width: 768px) {
          .fc-wrapper { padding: 16px 56px 16px 16px; }
          .fc-header { grid-template-columns: 1fr; gap: 12px; }
          .fc-stepper-track { flex-wrap: nowrap; overflow-x: auto; justify-content: flex-start; -webkit-overflow-scrolling: touch; }
          .fc-stepper-item { width: 62px; flex-shrink: 0; }
          .fc-stepper-line { width: 20px; flex-shrink: 0; }
          /* Camera first (primary action), then progress/continue, tips last */
          .fc-center { flex-basis: 100%; min-width: 0; order: -1; }
          .fc-right { order: 0; }
          .fc-left { order: 1; }
          .fc-left-heading { font-size: 19px; line-height: 24px; }
        }

        @media (max-width: 480px) {
          .fc-wrapper { padding: 12px 52px 12px 12px; }
          .fc-camera-actions { gap: 10px; }
          .fc-btn-primary, .fc-btn-outline { width: 100%; justify-content: center; }
          .fc-camera-actions > * { flex: 1 1 100%; }
          .fc-thumbs-row { gap: 6px; }
          .fc-chip-row { gap: 6px; padding: 10px 12px; }
          .fc-chip { font-size: 11px; padding: 5px 10px; }
        }
      `}</style>

      <div className="fc-wrapper">
        <div className="fc-header">
          <button className="fc-back" onClick={() => navigate(-1)}>
            <ChevronLeft size={20} />
            Face Scan
          </button>

          <div className="fc-stepper">
            <span className="fc-stepper-caption">Step 1 of 4</span>
            <div className="fc-stepper-track">
              {STEPPER.map((label, i) => (
                <React.Fragment key={label}>
                  {i > 0 && <span className="fc-stepper-line" />}
                  <div className={`fc-stepper-item${i === 0 ? ' active' : ''}`}>
                    <span className="fc-stepper-circle">{i + 1}</span>
                    <span className="fc-stepper-label">{label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          <span />
        </div>

        <div className="fc-content">
          {/* LEFT */}
          <div className="fc-left">
            <div>
              <h1 className="fc-left-heading">
                Let's capture your
                <span className="fc-left-heading-accent">best skin today!</span>
              </h1>
              <p className="fc-left-desc">You can capture your photo using camera or upload from your device.</p>
            </div>

            <div className="fc-card">
              <div className="fc-tips-title">
                <img src={forBestResultIcon} alt="" width={20} height={20} />
                For best results
              </div>
              <div className="fc-tips-list">
                {BEST_RESULT_TIPS.map(({ icon, title, desc }) => (
                  <div className="fc-tip-row" key={title}>
                    <img src={icon} alt="" className="fc-tip-icon" />
                    <div>
                      <div className="fc-tip-title">{title}</div>
                      <div className="fc-tip-desc">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="fc-card fc-upload-card">
              <img src={preferUploadIcon} alt="" className="fc-upload-icon" />
              <div>
                <div className="fc-upload-title">Prefer uploading images?</div>
                <div className="fc-upload-desc">You can upload clear photos from your device.</div>
              </div>
            </div>
          </div>

          {/* CENTER */}
          <div className="fc-center">
            <div className="fc-camera-card">
            <div className="fc-camera-frame">
              {phase === 'live' && !allDone && (
                <>
                  {cameraError ? (
                    <div className="fc-camera-error">{cameraError}</div>
                  ) : (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`fc-camera-video${facingMode === 'user' ? ' mirrored' : ''}`}
                    />
                  )}
                  <svg className="fc-face-guide-svg" viewBox="0 0 100 130" preserveAspectRatio="xMidYMid meet">
                    <ellipse
                      cx="50"
                      cy="65"
                      rx="46"
                      ry="62"
                      fill="none"
                      stroke={faceGuide.color}
                      strokeWidth="2.5"
                      style={{ filter: `drop-shadow(0 0 6px ${faceGuide.color}88)` }}
                    />
                  </svg>
                  <div className="fc-camera-top-controls">
                    <button className="fc-pill-btn" type="button">
                      <img src={autoIcon} alt="" width={13} height={13} />
                      Auto
                    </button>
                    <button className="fc-pill-btn" type="button" onClick={handleFlip}>
                      <img src={flipIcon} alt="" width={13} height={13} />
                      Flip
                    </button>
                  </div>
                  {!cameraError && (
                    <div className="fc-status-banner" style={{ background: captureNotReady ? '#C1580E' : faceGuide.color }}>
                      {captureNotReady ? 'Camera is still starting up — hold on a second and try again.' : faceGuide.message}
                    </div>
                  )}
                </>
              )}

              {phase === 'captured' && (
                <>
                  <img src={images[currentAngle.key]} alt={`${currentAngle.title} capture`} className="fc-camera-photo" />
                  <div className="fc-status-banner">
                    <img src={captureSuccessIcon} alt="" width={18} height={17} />
                    {getStatusMessage(currentAngle, images, allDone)}
                  </div>
                </>
              )}
            </div>

            <div className="fc-camera-bottom">
            <div className="fc-camera-actions">
              {phase === 'live' && !allDone && (
                <button className="fc-btn-primary" onClick={handleCapturePhoto}>
                  <CameraIcon size={16} />
                  Capture Photo
                </button>
              )}

              {phase === 'captured' && (
                <span className="fc-btn-outline-wrapper">
                  <button className="fc-btn-outline" onClick={handleRetake}>
                    <RotateCcw size={16} color="#004276" />
                    <span className="fc-btn-outline-text">Retake</span>
                  </button>
                </span>
              )}

              {phase === 'captured' && nextAngle && (
                <button className="fc-btn-primary" onClick={() => handleGoToNext(nextAngle)}>
                  <CameraIcon size={16} />
                  {`Capture Next (${nextAngle.title.split(' ')[0]})`}
                </button>
              )}

              {phase === 'live' && !allDone && (
                <span className="fc-btn-outline-wrapper">
                  <button className="fc-btn-outline" onClick={handleUploadClick}>
                    <img src={uploadButtonIcon} alt="" width={18} height={18} />
                    <span className="fc-btn-outline-text">Upload Image</span>
                  </button>
                </span>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />

            <div className="fc-secure-note">
              <img src={securePrivateIcon} alt="" width={16} height={16} />
              Your photos are secure and private
            </div>
            </div>
            </div>

            <div className="fc-chip-row">
              <span className="fc-chip fc-chip-ai">
                <img src={aiTipsIcon} alt="" className="fc-chip-icon" />
                AI Tips
              </span>
              {BOTTOM_CHIPS.map(({ icon, label }, i) => (
                <span className={`fc-chip ${i % 2 === 0 ? 'fc-chip-good' : 'fc-chip-hold'}`} key={label}>
                  <img src={icon} alt="" className="fc-chip-icon" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div className="fc-right">
            <div className="fc-card">
              <div className="fc-card-title">Capture Steps</div>
              <div className="fc-card-subtitle">Capture all 3 angles for an accurate analysis</div>

              {ANGLES.map((angle, i) => {
                const done = Boolean(images[angle.key]);
                return (
                  <button
                    type="button"
                    className={`fc-step-row${done ? ' done' : ''}${i === stepIndex ? ' current' : ''}`}
                    key={angle.key}
                    onClick={() => handleJumpToAngle(i)}
                  >
                    <img src={stepEmptyIcon} alt="" className="fc-step-icon" />
                    <div>
                      <div className="fc-step-title">{angle.title}</div>
                      <div className="fc-step-desc">{angle.capStep}</div>
                    </div>
                    {done ? (
                      <img src={capturedTickIcon} alt="" className="fc-step-check" />
                    ) : (
                      <ChevronRight size={16} color="#C9C4D9" style={{ marginLeft: 'auto', flexShrink: 0 }} />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="fc-card">
              <div className="fc-card-title">Captured Images</div>
              <div className="fc-thumbs-caption">{capturedCount} of {ANGLES.length} completed</div>
              <div className="fc-thumbs-row">
                {ANGLES.map((angle, i) => (
                  <button type="button" className="fc-thumb" key={angle.key} onClick={() => handleJumpToAngle(i)}>
                    <div className="fc-thumb-box">
                      {images[angle.key] ? (
                        <>
                          <img src={images[angle.key]} alt={angle.title} className="fc-thumb-photo" />
                          <img src={capturedTickIcon} alt="" className="fc-thumb-tick" />
                        </>
                      ) : (
                        <img src={captureEmptyIcon} alt="" className="fc-thumb-empty" />
                      )}
                    </div>
                    <span className={`fc-thumb-label${images[angle.key] ? ' done' : ''}`}>{angle.title.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <button
                className={`fc-continue-btn${canContinue ? ' enabled' : ''}`}
                disabled={!canContinue || isUploading}
                onClick={handleContinueToForm}
              >
                {isUploading && <Loader2 size={16} className="fc-spin" />}
                {isUploading ? 'Uploading…' : canContinue ? 'Continue to Form Page' : 'Continue'}
                {!isUploading && canContinue && <img src={continueArrowIcon} alt="" width={16} height={16} />}
              </button>
              {canContinue && !uploadError && !isUploading && (
                <div className="fc-continue-caption">
                  <img src={completeAllInfoIcon} alt="" width={13} height={13} />
                  Completed all 3 angles, you can continue now
                </div>
              )}
              {!canContinue && !uploadError && (
                <div className="fc-continue-caption">
                  <img src={completeAllInfoIcon} alt="" width={13} height={13} />
                  Capture all 3 angles ({capturedCount}/{ANGLES.length}) to continue
                </div>
              )}
              {uploadError && (
                <div className="fc-continue-caption" style={{ color: '#D14343' }}>
                  {uploadError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default CosmeticFaceCapture;
